type CacheItem<T> = {
    item: T
    accessed: Date
}

export class LeastRecentlyUsedCache<T> {
    private cache: Map<string, CacheItem<T>>
    private readonly maxSize: number

    constructor(maxSize: number, jsonString?: string | null) {
        this.maxSize = maxSize
        this.cache = new Map()

        this.fromJSON(jsonString || '[]')
    }

    // get returns an item from the cache and updates its access date
    public get(key: string): T | undefined {
        const value = this.cache.get(key)
        if (value) {
            this.cache.delete(key)
            value.accessed = new Date()
            this.cache.set(key, value)
        }
        return value?.item
    }

    // set adds an item to the cache and evicts the oldest item if the cache is full
    public set(key: string, value: T): void {
        // Set the item with the current date
        this._setWithDate(key, value, new Date())
        this._purgeOverLimit()
    }

    // Used dynamically when unit testing
    public _getWithoutUpdate(key: string): T | undefined {
        return this.cache.get(key)?.item
    }

    public _setWithDate(key: string, value: T, date: Date | string): void {
        this.cache.set(key, { item: value, accessed: new Date(date) })
    }

    private _purgeOverLimit(): void {
        // If we're within the size limit, return
        if (this.cache.size <= this.maxSize) {
            return
        }

        // Calculate how many we have to remove
        const removeCount = this.cache.size - this.maxSize

        // Sort the keys by ascending date
        const keysByDate = [...this.cache.entries()].sort(([_keyA, dataA], [_keyB, dataB]) => {
            return new Date(dataA.accessed).getTime() - new Date(dataB.accessed).getTime()
        })
        // Delete from the cache as many keys as we need to remove
        for (let i = 0; i < removeCount; i++) {
            this.cache.delete(keysByDate[i][0])
        }
    }

    toJSON(): string {
        return JSON.stringify([...this.cache])
    }

    fromJSON(json: string): void {
        JSON.parse(json).forEach((entry: [string, CacheItem<T>]) => {
            this._setWithDate(entry[0], entry[1].item, entry[1].accessed)
        })
        this._purgeOverLimit()
    }
}
